import { useEffect, useState, useRef } from "react";
import axiosInstance from "../AxiosInstance";
import { useTranslation } from "react-i18next";

const News = () => {
  const { t } = useTranslation();
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);

  // Helper function to check if file is PDF
  const isPdfFile = (url) => {
    if (!url) return false;
    return url.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('.pdf');
  };

  // Helper function to download PDF
  const handleDownloadPdf = async (pdfUrl, fileName) => {
    try {
      // Ensure we have a proper filename with .pdf extension
      let downloadFileName = fileName || 'document.pdf';
      if (!downloadFileName.toLowerCase().endsWith('.pdf')) {
        downloadFileName = downloadFileName + '.pdf';
      }

      // Determine the correct URL to use for download
      let downloadUrl = pdfUrl;
      
      // If photoUrl is a full URL, use it directly with fetch
      // If it's a relative path, construct full URL
      if (!pdfUrl.startsWith('http://') && !pdfUrl.startsWith('https://')) {
        // It's a relative path, construct full URL
        const baseURL = axiosInstance.defaults.baseURL || 'https://visiga-seminary.com/api/v1';
        // Remove leading slash if present to avoid double slashes
        const cleanPath = pdfUrl.startsWith('/') ? pdfUrl.substring(1) : pdfUrl;
        downloadUrl = `${baseURL}/${cleanPath}`;
      }

      // Download the file using fetch (works with both full URLs and relative paths)
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf, */*',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Create blob from response
      const blob = await response.blob();
      
      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      // Fallback: try to open in new tab
      try {
        const fullUrl = pdfUrl.startsWith('http') 
          ? pdfUrl 
          : `${axiosInstance.defaults.baseURL}/${pdfUrl.startsWith('/') ? pdfUrl.substring(1) : pdfUrl}`;
        window.open(fullUrl, '_blank');
      } catch (fallbackError) {
        console.error('Fallback download also failed:', fallbackError);
        alert('Failed to download PDF. Please try again.');
      }
    }
  };

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const response = await axiosInstance.get("/files/page/NEWS");
        // Sort by date (newest first) - if date is not available, sort by id (highest first) as fallback
        const newsItems = response.data
          .sort((a, b) => {
            // First try to sort by date (newest first)
            if (a.date && b.date) {
              return new Date(b.date) - new Date(a.date);
            }
            // Fallback: sort by id (highest first, assuming higher id = newer)
            if (a.id && b.id) {
              return b.id - a.id;
            }
            // If neither date nor id, keep original order
            return 0;
          });
        setNewsItems(newsItems);
      } catch (error) {
        console.error("Failed to fetch photos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  // Scroll to the left (start) when news items are loaded or updated
  useEffect(() => {
    if (!loading && scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [loading, newsItems]);

  return (
    <div className="mt-0 tracking-wide bg-[#85c4de] text-white py-5 dark:bg-gray-900">
      <h2 className="text-3xl sm:text-5xl lg:text-6xl text-center my-1 lg:my-2 bg-gradient-to-r from-blue-500 to-orange-800 text-transparent bg-clip-text">
        {t("newsTitle")}
      </h2>

      {loading ? (
        <div className="flex justify-center items-center h-[300px]">
          <div className="w-12 h-12 border-4 border-dashed rounded-full animate-spin border-white border-t-transparent"></div>
        </div>
      ) : (
        <div 
          ref={scrollContainerRef}
          className="overflow-x-auto scroll-smooth snap-x snap-mandatory whitespace-nowrap px-4"
        >
          <div className="flex gap-4">
            {newsItems.map((news, index) => (
              <div
                key={index}
                className="snap-start w-[85vw] flex-shrink-0 bg-neutral-300 rounded-lg p-4 border border-neutral-800 shadow-lg sm:w-[500px] md:w-[400px]"
              >
                {news.photoUrl && (
                  <div className="mb-4">
                    {isPdfFile(news.photoUrl) ? (
                      // PDF Display
                      <div className="relative rounded-md border border-neutral-300 w-full bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 p-6 flex flex-col items-center justify-center min-h-[200px]">
                        <div className="flex flex-col items-center gap-3">
                          {/* PDF Icon */}
                          <svg className="w-20 h-20 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">PDF Document</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 text-center px-2 truncate w-full">
                            {news.name || news.title || 'Document'}
                          </p>
                        </div>
                        {/* Download Button */}
                        <button
                          onClick={() => handleDownloadPdf(news.photoUrl, news.name || news.title || 'document.pdf')}
                          className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center gap-2 transition-colors shadow-md"
                          title="Download PDF"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          <span className="text-sm font-medium">Download</span>
                        </button>
                      </div>
                    ) : (
                      // Image Display
                      <img
                        className="rounded-md border border-neutral-300 w-full object-cover max-h-[200px]"
                        src={news.photoUrl}
                        alt={news.name || "News"}
                      />
                    )}
                  </div>
                )}
                <div className="flex items-start mb-3">
                  <div className="bg-blue-500 text-white rounded-full w-10 h-10 flex-shrink-0 flex items-center justify-center mr-4">
                    📰
                  </div>
                  <div className="min-w-0">
                    <h6 className="font-medium text-black text-lg break-words whitespace-normal">
                      {news.title}
                    </h6>
                    <span className="text-sm font-normal italic text-neutral-600">
                      {news.name}
                    </span>
                  </div>
                </div>
                <p className="text-black break-words whitespace-normal">
                  {news.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default News;
